import { apiClient } from './client';
import type { ApiSuccess, PaginationMeta } from '../types';

export interface ListResult<T> {
  data: T[];
  meta: PaginationMeta;
}

export interface ActivityItem {
  id: number;
  action: string;
  actorUserId: number | null;
  actorLabel: string | null;
  createdAt: string;
}

/** Builds a typed CRUD client for a REST resource following the standard API contract. */
export function createCrudApi<T, CreateInput, UpdateInput = Partial<CreateInput>>(resource: string) {
  return {
    async list(params: Record<string, unknown> = {}): Promise<ListResult<T>> {
      const { data } = await apiClient.get<ApiSuccess<T[]>>(`/${resource}`, { params });
      return { data: data.data, meta: data.meta as PaginationMeta };
    },
    async get(id: number): Promise<T> {
      const { data } = await apiClient.get<ApiSuccess<T>>(`/${resource}/${id}`);
      return data.data;
    },
    async create(input: CreateInput): Promise<T> {
      const { data } = await apiClient.post<ApiSuccess<T>>(`/${resource}`, input);
      return data.data;
    },
    async update(id: number, input: UpdateInput): Promise<T> {
      const { data } = await apiClient.put<ApiSuccess<T>>(`/${resource}/${id}`, input);
      return data.data;
    },
    async remove(id: number): Promise<void> {
      await apiClient.delete(`/${resource}/${id}`);
    },
    async activity(id: number): Promise<ActivityItem[]> {
      const { data } = await apiClient.get<ApiSuccess<ActivityItem[]>>(`/${resource}/${id}/activity`);
      return data.data;
    },
  };
}
